import pako from "pako";

export interface CompressedPayload {
  compressed: true;
  data: string; // Base64-encoded compressed data
  original_size: number; // Size before compression (for logging)
}

export interface DecompressionResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  compressionRatio?: number;
}

/**
 * Decompress a Zlib-compressed, Base64-encoded payload.
 *
 * Backend compresses payloads > 8KB using:
 * 1. Zlib::Deflate.deflate() - Ruby's Zlib compression
 * 2. Base64.strict_encode64() - Base64 encoding for JSON transmission
 *
 * Frontend must reverse this process:
 * 1. Base64 decode to get binary data
 * 2. Zlib inflate to get original JSON string
 * 3. JSON parse to get object
 *
 * @param compressedPayload - The compressed payload from backend
 * @returns Decompression result with data or error
 */
export function decompressPayload<T>(
  compressedPayload: CompressedPayload,
): DecompressionResult<T> {
  const startTime = performance.now();

  try {
    // Step 1: Base64 decode to binary data
    const base64Data = compressedPayload.data;
    const binaryString = atob(base64Data);

    // Convert binary string to Uint8Array
    const binaryData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      binaryData[i] = binaryString.charCodeAt(i);
    }

    // Step 2: Zlib inflate (decompress)
    const decompressedData = pako.inflate(binaryData, { to: "string" });

    // Step 3: JSON parse
    const parsedData = JSON.parse(decompressedData) as T;

    // Calculate metrics
    const decompressedSize = decompressedData.length;
    const compressedSize = base64Data.length;
    const compressionRatio = (
      (1 - compressedSize / decompressedSize) *
      100
    ).toFixed(1);

    return {
      success: true,
      data: parsedData,
      compressionRatio: parseFloat(compressionRatio),
    };
  } catch (error) {
    const duration = performance.now() - startTime;
    console.error(
      `[PayloadDecompressor] Decompression failed after ${duration.toFixed(
        2,
      )}ms:`,
      error,
    );

    return {
      success: false,
      error: error as Error,
    };
  }
}

/**
 * Check if a payload is compressed.
 *
 * @param payload - Payload to check
 * @returns True if payload has compressed flag
 */
export function isCompressedPayload(
  payload: unknown,
): payload is CompressedPayload {
  if (typeof payload !== "object" || payload === null) {
    return false;
  }

  const p = payload as Record<string, unknown>;

  // Check for compressed flag and required fields
  return (
    p.compressed === true &&
    typeof p.data === "string" &&
    typeof p.original_size === "number"
  );
}

/**
 * Process a payload that may or may not be compressed.
 *
 * This is the main entry point for handling payloads from backend.
 * Automatically detects compression and decompresses if needed.
 *
 * @param payload - Payload from backend (compressed or uncompressed)
 * @returns Processed payload data
 * @throws Error if decompression fails
 */
export function processPayload<T>(payload: unknown): T {
  // Check if payload is compressed
  if (isCompressedPayload(payload)) {
    const result = decompressPayload<T>(payload);

    if (!result.success || !result.data) {
      throw new Error(
        `Failed to decompress payload: ${
          result.error?.message || "Unknown error"
        }`,
      );
    }

    return result.data;
  }

  return payload as T;
}
